import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from '../../theme/swal';
import { IcMessageCircle, IcClose, IcSend, IcTrash, IcUserCircle } from '../../theme/icons';
import noteService from '../../services/noteService';
import authService from '../../services/authService';
import KI from '../../theme/palette';

// Same seen-marker as the desktop chat so read state is shared on the device.
const SEEN_KEY = 'dashboard_notes_seen_id';
const POLL_MS = 15000;

// Couleur d'auteur = rampes de la palette Ki Para uniquement.
const AUTHOR_COLORS = ['text-red-600', 'text-blue-600', 'text-green-600', 'text-amber-600', 'text-amber-700', 'text-green-700'];
const authorColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 997;
  return AUTHOR_COLORS[h % AUTHOR_COLORS.length];
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Aujourd'hui";
  if (same(d, yest)) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

const timeLabel = (iso) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/**
 * Mobile Notes — the same discussion thread between accounts as the desktop
 * dashboard chat, presented as a slide-up sheet. Stays mounted (polls in the
 * background) so the shell can show an unread badge on the ⋮ menu.
 */
export default function MobileNotesSheet({ open, onClose, onUnreadChange }) {
  const user = authService.getCurrentUser();
  const myId = user?.user_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'manager'; // accès complet

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const listRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;
  const onUnreadRef = useRef(onUnreadChange);
  onUnreadRef.current = onUnreadChange;

  const markSeen = (list) => {
    const lastId = list.length ? list[list.length - 1].id : 0;
    if (lastId) localStorage.setItem(SEEN_KEY, String(lastId));
    onUnreadRef.current?.(0);
  };

  const computeUnread = (list) => {
    const seen = Number(localStorage.getItem(SEEN_KEY) || 0);
    return list.filter((n) => n.id > seen && n.author_id !== myId).length;
  };

  const fetchNotes = useCallback(async () => {
    try {
      const list = await noteService.getNotes();
      const arr = Array.isArray(list) ? list : [];
      setNotes(arr);
      if (openRef.current) markSeen(arr);
      else onUnreadRef.current?.(computeUnread(arr));
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNotes();
    const t = setInterval(fetchNotes, POLL_MS);
    return () => clearInterval(t);
  }, [fetchNotes]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, notes]);

  useEffect(() => {
    if (open) markSeen(notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const created = await noteService.addNote(content);
      setDraft('');
      setNotes((prev) => {
        const next = [...prev, created];
        markSeen(next);
        return next;
      });
    } catch (err) {
      console.error('Error sending note:', err);
      Swal.fire('Erreur', "Impossible d'envoyer la note.", 'error');
    } finally {
      setSending(false);
    }
  };

  const remove = async (note) => {
    const res = await Swal.fire({
      title: 'Supprimer cette note ?',
      text: note.content.length > 80 ? `${note.content.slice(0, 80)}…` : note.content,
      icon: 'warning', showCancelButton: true, confirmButtonColor: KI.coralDark,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
    });
    if (!res.isConfirmed) return;
    try {
      await noteService.deleteNote(note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
    } catch (err) {
      console.error('Error deleting note:', err);
      Swal.fire('Erreur', 'Impossible de supprimer la note.', 'error');
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-black/40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <div
        role="dialog"
        aria-hidden={!open}
        className={`fixed z-50 inset-0 flex flex-col bg-white transition-transform duration-300 ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Header */}
        <div
          className="shrink-0 bg-gradient-to-b from-gray-900 to-black text-white px-4 py-3 flex items-center justify-between"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <IcMessageCircle className="text-gray-200 shrink-0" />
            <div className="min-w-0">
              <h2 className="font-semibold leading-tight truncate">Notes — Discussion</h2>
              <p className="text-[11px] text-white/60 truncate">Visible par tous les comptes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="p-2 -mr-1 rounded-lg text-white/70 active:text-white active:bg-white/10 transition-colors"
          >
            <IcClose />
          </button>
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-y-auto bg-gray-50 px-3 py-3 space-y-2">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600" />
            </div>
          ) : notes.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <IcMessageCircle className="text-4xl text-gray-200 mb-2" />
              <p className="text-sm text-gray-400">Aucune note pour le moment.</p>
              <p className="text-xs text-gray-400 mt-1">Écrivez la première note pour l'équipe.</p>
            </div>
          ) : (
            notes.map((n, i) => {
              const mine = n.author_id === myId;
              const prevN = notes[i - 1];
              const newDay = !prevN || dayLabel(prevN.created_at) !== dayLabel(n.created_at);
              const sameAuthorAsPrev = prevN && prevN.author_id === n.author_id && !newDay;
              return (
                <React.Fragment key={n.id}>
                  {newDay && (
                    <div className="flex justify-center py-1">
                      <span className="px-2.5 py-0.5 rounded-full bg-gray-200 text-gray-500 text-[10px] font-semibold">
                        {dayLabel(n.created_at)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className="relative max-w-[78%]">
                      {!mine && !sameAuthorAsPrev && (
                        <p className={`flex items-center gap-1 text-[11px] font-semibold mb-0.5 ml-1 ${authorColor(n.author_name)}`}>
                          <IcUserCircle size={11} /> {n.author_name || 'Compte supprimé'}
                        </p>
                      )}
                      {/* Ma bulle = noir encre (l'aplat rouge #DC2626 est réservé
                          à l'impayé / rupture — unique dans tout le site). */}
                      <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm ${
                        mine
                          ? 'bg-gray-900 text-white rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                      }`}
                      >
                        {n.content}
                        <span className={`block text-right text-[10px] mt-0.5 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                          {timeLabel(n.created_at)}
                        </span>
                      </div>
                      {(mine || isAdmin) && (
                        <button
                          onClick={() => remove(n)}
                          aria-label="Supprimer la note"
                          className={`absolute top-1 ${mine ? '-left-8' : '-right-8'} p-2 rounded-full text-red-600 active:bg-red-50 transition-colors`}
                        >
                          <IcTrash size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Input */}
        <div className="shrink-0 border-t border-gray-200 bg-white p-3 flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={1}
            placeholder="Écrire une note…"
            aria-label="Écrire une note"
            className="inp flex-1 resize-none max-h-28 rounded-xl px-3 py-2.5 text-base"
          />
          <button
            onClick={send}
            disabled={sending || !draft.trim()}
            aria-label="Envoyer la note"
            className="btn-ink shrink-0 w-11 h-11 rounded-xl transition-colors"
          >
            {sending
              ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              : <IcSend />}
          </button>
        </div>
      </div>
    </>
  );
}
